/**
 * Unified Persistence Serce
 * Manages character data persistence across local storage and Google Drive
 */

import type { Character } from '../types/character';
import {
  SaveResult,
  LoadResult,
  ConflictResolution,
  ConflictResolutionType,
  SyncOperation,
  SyncOperationType,
  StorageLocation,
  Summary
} from '../types/persistence';
import { localStorageService, LocalStorageService } from './LocalStorageService';
import { getGoogleDriveService } from './GoogleDriveServiceFactory';
import { calculateChecksum } from '../utils/checksum';

// Type for the Google Drive service interface
type GoogleDriveServiceInterface = ReturnType<typeof getGoogleDriveService>;

interface PersistenceSettings {
  defaultLocation: StorageLocation;
  autoSync: boolean;
  syncInterval: number;
  conflictResolution: ConflictResolutionType;
}

interface SyncState {
  isOnline: boolean;
  lastSync: Date | null;
  pendingOperations: SyncOperation[];
  conflicts: ConflictResolution[];
}

export class PersistenceService {
  private settings: PersistenceSettings;
  private syncState: SyncState;
  private syncInterval: number | null = null;
  private onlineStatusListener: (() => void) | null = null;
  private localStorageService: LocalStorageService;
  private googleDriveService: GoogleDriveServiceInterface;

  constructor(
    localStorageService?: LocalStorageService,
    googleDriveService?: GoogleDriveServiceInterface
  ) {
    this.localStorageService = localStorageService;
    this.googleDriveService = googleDriveService;
    this.settings = this.loadSettings();
    this.syncState = {
      isOnline: navigator.onLine,
      lastSync: null,
      pendingOperations: [],
      conflicts: []
    };

    this.initializeOnlineStatusListener();
    this.startAutoSync();
  }

  /**
   * Save character to specified location or default location
   */
  async save(character: Character, location?: StorageLocation): Promise<SaveResult> {
    // Handle array of locations
    if (Array.isArray(location)) {
      const results: SaveResult[] = [];
      const locations: StorageLocation[] = [];
      const warnings: string[] = [];

      for (const loc of location) {
        const result = await this.save(character, loc);
        results.push(result);
        if (result.success) {
          locations.push(loc);
        } else if (result.error) {
          warnings.push(`Failed to save to ${loc}: ${result.error}`);
        }
      }

      const hasSuccess = results.some((r) => r.success);
      return {
        success: hasSuccess,
        location: locations[0] || location[0],
        locations,
        warnings: warnings.length > 0 ? warnings : undefined,
        timestamp: new Date(),
        error: hasSuccess ? undefined : 'Failed to save to all locations'
      };
    }

    const targetLocation = location || this.settings.defaultLocation;

    try {
      let result: SaveResult;

      switch (targetLocation) {
        case StorageLocation.Local:
          result = await (this.localStorageService || localStorageService).save(character);
          break;

        case StorageLocation.GoogleDrive:
          if (!this.syncState.isOnline) {
            // Queue for sync when online
            this.queueSyncOperation({
              id: `save-${character.id}-${Date.now()}`,
              type: SyncOperationType.Save,
              characterId: character.id,
              timestamp: new Date(),
              retryCount: 0,
              maxRetries: 3,
              data: character
            });

            // Save locally as backup
            await (this.localStorageService || localStorageService).save(character);

            return {
              success: true,
              location: StorageLocation.GoogleDrive,
              timestamp: new Date(),
              error: 'Saved locally - will sync to Google Drive when online'
            };
          }

          result = await (this.googleDriveService || getGoogleDriveService()).save(character);

          // Also save locally as backup if Google Drive save succeeded
          if (result.success) {
            await (this.localStorageService || localStorageService).save(character);
          }
          break;

        default:
          throw new Error(`Unsupported storage location: ${targetLocation}`);
      }

      // Update sync state on successful save
      if (result.success && targetLocation === StorageLocation.GoogleDrive) {
        this.syncState.lastSync = new Date();
      }

      return result;
    } catch (error) {
      console.error('Failed to save character:', error);
      return {
        success: false,
        location: targetLocation,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown save error'
      };
    }
  }

  /**
   * Load character from specified location or try both locations
   */
  async load(characterId: string, location?: StorageLocation): Promise<LoadResult> {
    try {
      if (location) {
        // Load from specific location
        switch (location) {
          case StorageLocation.Local:
            return await (this.localStorageService || localStorageService).load(characterId);

          case StorageLocation.GoogleDrive:
            if (!this.syncState.isOnline) {
              return {
                success: false,
                location: StorageLocation.GoogleDrive,
                timestamp: new Date(),
                error: 'Cannot load from Google Drive while offline'
              };
            }
            return await (this.googleDriveService || getGoogleDriveService()).load(characterId);

          default:
            throw new Error(`Unsupported storage location: ${location}`);
        }
      }

      // Try to load from both locations and handle conflicts
      const [localResult, driveResult] = await Promise.allSettled([
        (this.localStorageService || localStorageService).load(characterId),
        this.syncState.isOnline
          ? (this.googleDriveService || getGoogleDriveService()).load(characterId)
          : Promise.reject(new Error('Offline'))
      ]);

      // Handle different scenarios
      if (localResult.status === 'fulfilled' && localResult.value.success) {
        if (driveResult.status === 'fulfilled' && driveResult.value.success) {
          // Both versions exist - check for conflicts
          const conflict = await this.detectConflict(
            localResult.value.character!,
            driveResult.value.character!
          );

          if (conflict) {
            this.syncState.conflicts.push(conflict);
            // Return local version but indicate conflict
            return {
              ...localResult.value,
              error: 'Conflict detected with Google Drive version'
            };
          }

          // No conflict - return the newer version
          const localTime = localResult.value.character!.lastModified.getTime();
          const driveTime = driveResult.value.character!.lastModified.getTime();

          return driveTime > localTime ? driveResult.value : localResult.value;
        }

        // Only local version exists
        return localResult.value;
      }

      if (driveResult.status === 'fulfilled' && driveResult.value.success) {
        // Only Google Drive version exists
        const result = driveResult.value;

        // Save to local storage as backup
        if (result.character) {
          await (this.localStorageService || localStorageService).save(result.character);
        }

        return result;
      }

      // Neither version exists or both failed
      return {
        success: false,
        location: StorageLocation.Local,
        timestamp: new Date(),
        error: 'not found in any storage location'
      };
    } catch (error) {
      console.error('Failed to load character:', error);
      return {
        success: false,
        location: StorageLocation.Local,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown load error'
      };
    }
  }

  /**
   * Delete character from specified location or all locations
   */
  async delete(characterId: string, location?: StorageLocation): Promise<SaveResult> {
    try {
      const results: SaveResult[] = [];

      if (!location || location === StorageLocation.Local) {
        const localResult = await (this.localStorageService || localStorageService).delete(
          characterId
        );
        results.push(localResult);
      }

      if (!location || location === StorageLocation.GoogleDrive) {
        if (this.syncState.isOnline) {
          // TODO: Implement delete method in GoogleDriveService
          // const driveResult = await (this.googleDriveService || getGoogleDriveService()).delete(characterId);
          const driveResult = {
            success: true,
            location: StorageLocation.GoogleDrive,
            timestamp: new Date()
          };
          results.push(driveResult);
        } else {
          // Queue for deletion when online
          this.queueSyncOperation({
            id: `delete-${characterId}-${Date.now()}`,
            type: SyncOperationType.Delete,
            characterId,
            timestamp: new Date(),
            retryCount: 0,
            maxRetries: 3
          });
        }
      }

      // Return success if any deletion succeeded
      const hasSuccess = results.some((result) => result.success);
      const errors = results
        .filter((result) => !result.success)
        .map((result) => result.error)
        .filter(Boolean);

      return {
        success: hasSuccess,
        location: location || StorageLocation.Local,
        timestamp: new Date(),
        error: errors.length > 0 ? errors.join('; ') : undefined
      };
    } catch (error) {
      console.error('Failed to delete character:', error);
      return {
        success: false,
        location: location || StorageLocation.Local,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown delete error'
      };
    }
  }

  /**
   * List characters from all available locations
   */
  async lists(): Promise<Summary[]> {
    try {
      const [localSummaries, driveSummaries] = await Promise.allSettled([
        (this.localStorageService || localStorageService).lists(),
        this.syncState.isOnline
          ? (this.googleDriveService || getGoogleDriveService()).list()
          : Promise.resolve([])
      ]);

      const local = localSummaries.status === 'fulfilled' ? localSummaries.value : [];
      const drive = driveSummaries.status === 'fulfilled' ? driveSummaries.value : [];

      // Merge and deduplicate summaries
      const merged = new Map<string, Summary>();

      // Add local summaries
      local.forEach((summary) => {
        merged.set(summary.id, {
          ...summary,
          syncStatus: this.get(summary.id)
        });
      });

      // Add or update with drive summaries
      drive.forEach((summary) => {
        const existing = merged.get(summary.id);
        if (existing) {
          // Keep the newer version's metadata
          if (summary.lastModified > existing.lastModified) {
            merged.set(summary.id, {
              ...summary,
              syncStatus: this.get(summary.id)
            });
          }
        } else {
          merged.set(summary.id, {
            ...summary,
            syncStatus: this.get(summary.id)
          });
        }
      });

      return Array.from(merged.values()).sort(
        (a, b) => b.lastModified.getTime() - a.lastModified.getTime()
      );
    } catch (error) {
      console.error('Failed to list characters:', error);
      return [];
    }
  }

  /**
   * Sync character data between local storage and Google Drive
   */
  async sync(characterId: string): Promise<SaveResult> {
    try {
      if (!this.syncState.isOnline) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Cannot sync while offline'
        };
      }

      // Load from both locations
      const [localResult, driveResult] = await Promise.allSettled([
        (this.localStorageService || localStorageService).load(characterId),
        (this.googleDriveService || getGoogleDriveService()).load(characterId)
      ]);

      const localSuccess = localResult.status === 'fulfilled' && localResult.value.success;
      const driveSuccess = driveResult.status === 'fulfilled' && driveResult.value.success;

      if (localSuccess && driveSuccess) {
        // Both exist - check for conflicts
        const localChar = localResult.value.character!;
        const driveChar = driveResult.value.character!;

        const conflict = await this.detectConflict(localChar, driveChar);
        if (conflict) {
          this.syncState.conflicts.push(conflict);
          return {
            success: false,
            location: StorageLocation.GoogleDrive,
            timestamp: new Date(),
            error: 'Sync conflict detected - manual resolution required'
          };
        }

        // No conflict - sync the newer version
        const localTime = localChar.lastModified.getTime();
        const driveTime = driveChar.lastModified.getTime();

        if (localTime > driveTime) {
          // Local is newer - upload to drive
          return await (this.googleDriveService || getGoogleDriveService()).save(localChar);
        } else if (driveTime > localTime) {
          // Drive is newer - download to local
          await (this.localStorageService || localStorageService).save(driveChar);
          return {
            success: true,
            location: StorageLocation.GoogleDrive,
            timestamp: new Date()
          };
        }

        // Same timestamp - already synced
        return {
          success: true,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date()
        };
      }

      if (localSuccess && !driveSuccess) {
        // Only local exists - upload to drive
        return await (this.googleDriveService || getGoogleDriveService()).save(
          localResult.value.character!
        );
      }

      if (!localSuccess && driveSuccess) {
        // Only drive exists - download to local
        await (this.localStorageService || localStorageService).save(driveResult.value.character!);
        return {
          success: true,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date()
        };
      }

      // Neither exists
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: 'not found in any location'
      };
    } catch (error) {
      console.error('Failed to sync character:', error);
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Sync failed'
      };
    }
  }

  /**
   * Resolve a conflict between local and remote versions
   */
  async resolveConflict(
    conflictId: string,
    resolution: ConflictResolutionType,
    merged?: Character
  ): Promise<SaveResult> {
    const conflict = this.syncState.conflicts.find((c) => c.characterId === conflictId);
    if (!conflict) {
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: 'Conflict not found'
      };
    }

    try {
      let characterToSave: Character;

      switch (resolution) {
        case ConflictResolutionType.UseLocal:
          characterToSave = conflict.localVersion;
          break;
        case ConflictResolutionType.UseRemote:
          characterToSave = conflict.remoteVersion;
          break;
        case ConflictResolutionType.Merge:
          if (!merged) {
            throw new Error('Merged character required for merge resolution');
          }
          characterToSave = merged;
          break;
        default:
          throw new Error(`Unsupported resolution type: ${resolution}`);
      }

      // Save the resolved character to both locations
      const result = await this.save(characterToSave);

      if (result.success) {
        // Remove resolved conflict
        this.syncState.conflicts = this.syncState.conflicts.filter(
          (c) => c.characterId !== conflictId
        );
      }

      return result;
    } catch (error) {
      console.error('Failed to resolve conflict:', error);
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Conflict resolution failed'
      };
    }
  }

  /**
   * Get current sync status for a character
   */
  get(characterId: string): {
    synced: boolean;
    pending: boolean;
    conflict: boolean;
    error: boolean;
    offline: boolean;
    lastSyncTime?: Date;
    locations?: StorageLocation[];
    conflictDetails?: unknown;
  } {
    const hasConflict = this.syncState.conflicts.some((c) => c.characterId === characterId);
    const hasPendingOp = this.syncState.pendingOperations.some(
      (op) => op.characterId === characterId
    );

    const conflict = this.syncState.conflicts.find((c) => c.characterId === characterId);

    return {
      synced: !hasConflict && !hasPendingOp && this.syncState.isOnline,
      pending: hasPendingOp,
      conflict: hasConflict,
      error: false, // TODO: Track sync errors
      offline: !this.syncState.isOnline,
      lastSyncTime: this.syncState.lastSync,
      locations: [StorageLocation.Local, StorageLocation.GoogleDrive],
      conflictDetails: conflict
    };
  }

  /**
   * Merge two character versions (for conflict resolution)
   */
  async merge(localVersion: Character, remoteVersion: Character): Promise<Character> {
    // Simple merge strategy - take the newer version's data but preserve local manual adjustments
    const newerVersion =
      localVersion.lastModified > remoteVersion.lastModified ? localVersion : remoteVersion;
    const olderVersion =
      localVersion.lastModified > remoteVersion.lastModified ? remoteVersion : localVersion;

    return {
      ...newerVersion,
      manualAdjustments: [
        ...olderVersion.manualAdjustments,
        ...newerVersion.manualAdjustments
      ].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
      lastModified: new Date()
    };
  }

  /**
   * Get all pending conflicts
   */
  getConflicts(): ConflictResolution[] {
    return [...this.syncState.conflicts];
  }

  /**
   * Get sync state information
   */
  getSyncState(): SyncState {
    return { ...this.syncState };
  }

  /**
   * Update persistence settings
   */
  updateSettings(newSettings: Partial<PersistenceSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();

    // Restart auto-sync if interval changed
    if (newSettings.syncInterval !== undefined || newSettings.autoSync !== undefined) {
      this.stopAutoSync();
      this.startAutoSync();
    }
  }

  /**
   * Get current settings
   */
  getSettings(): PersistenceSettings {
    return { ...this.settings };
  }

  // Private helper methods

  async detectConflict(local: Character, remote: Character): Promise<ConflictResolution | null> {
    const localTime = local.lastModified.getTime();
    const remoteTime = remote.lastModified.getTime();

    // If timestamps are different and both have been modified, it's a conflict
    if (Math.abs(localTime - remoteTime) > 1000) {
      // 1 second tolerance
      const localChecksum = calculateChecksum(JSON.stringify(local));
      const remoteChecksum = calculateChecksum(JSON.stringify(remote));

      if (localChecksum !== remoteChecksum) {
        return {
          characterId: local.id,
          localVersion: local,
          remoteVersion: remote,
          resolution: ConflictResolutionType.CreateCopy,
          timestamp: new Date()
        };
      }
    }

    return null;
  }

  private queueSyncOperation(operation: SyncOperation): void {
    this.syncState.pendingOperations.push(operation);
    console.log(`Queued sync operation: ${operation.type} for character ${operation.characterId}`);
  }

  private async processPendingOperations(): Promise<void> {
    if (!this.syncState.isOnline || this.syncState.pendingOperations.length === 0) {
      return;
    }

    const operations = [...this.syncState.pendingOperations];
    this.syncState.pendingOperations = [];

    for (const operation of operations) {
      try {
        await this.processSyncOperation(operation);
      } catch (error) {
        console.error(`Failed to process sync operation ${operation.id}:`, error);

        // Retry if under max retries
        if (operation.retryCount < operation.maxRetries) {
          operation.retryCount++;
          this.syncState.pendingOperations.push(operation);
        }
      }
    }
  }

  private async processSyncOperation(operation: SyncOperation): Promise<void> {
    switch (operation.type) {
      case SyncOperationType.Save:
        if (operation.data) {
          await (this.googleDriveService || getGoogleDriveService()).save(
            operation.data as Character
          );
        }
        break;

      case SyncOperationType.Delete:
        // TODO: Implement delete method in GoogleDriveService
        // await (this.googleDriveService || getGoogleDriveService()).delete(operation.characterId);
        break;

      default:
        throw new Error(`Unknown sync operation type: ${operation.type}`);
    }
  }

  private initializeOnlineStatusListener(): void {
    this.onlineStatusListener = () => {
      const wasOnline = this.syncState.isOnline;
      this.syncState.isOnline = navigator.onLine;

      if (!wasOnline && this.syncState.isOnline) {
        // Just came online - process pending operations
        console.log('Came online - processing pending sync operations');
        this.processPendingOperations();
      }
    };

    window.addEventListener('online', this.onlineStatusListener);
    window.addEventListener('offline', this.onlineStatusListener);
  }

  private startAutoSync(): void {
    if (!this.settings.autoSync) return;

    this.syncInterval = setInterval(() => {
      if (this.syncState.isOnline) {
        this.processPendingOperations();
      }
    }, this.settings.syncInterval) as unknown as number;
  }

  private stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  private loadSettings(): PersistenceSettings {
    try {
      const settingsData = localStorage.getItem('rogue-trader-persistence-settings');
      if (settingsData) {
        const settings = JSON.parse(settingsData);
        return {
          defaultLocation: settings.defaultLocation || StorageLocation.Local,
          autoSync: settings.autoSync ?? true,
          syncInterval: settings.syncInterval || 30000, // 30 seconds
          conflictResolution: settings.conflictResolution || ConflictResolutionType.CreateCopy
        };
      }
    } catch (error) {
      console.error('Failed to load persistence settings:', error);
    }

    return {
      defaultLocation: StorageLocation.Local,
      autoSync: true,
      syncInterval: 30000,
      conflictResolution: ConflictResolutionType.CreateCopy
    };
  }

  private saveSettings(): void {
    try {
      localStorage.setItem('rogue-trader-persistence-settings', JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save persistence settings:', error);
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopAutoSync();

    if (this.onlineStatusListener) {
      window.removeEventListener('online', this.onlineStatusListener);
      window.removeEventListener('offline', this.onlineStatusListener);
    }
  }
}

// Export singleton instance
export const persistenceService = new PersistenceService();
